/**
 * Budgets tab — the rules list, the create/edit form, and (added in the alerts
 * task) the alerts feed. Consistent with RunsTab / UsageTab: local `useState`,
 * no router, Saga-themed classes from index.css.
 *
 * The console remains READ-ONLY with respect to usage data — budget rules are
 * configuration, gated by the same write permission the ingest plane uses. A
 * server 403 on a mutation surfaces inline (it does NOT blank the console).
 *
 * Delete uses an in-page two-step confirm rather than window.confirm: a browser
 * modal blocks the page for automation and screenshotting.
 */

import { useState } from 'react';
import {
  useBudgets, useDeleteBudget, useUpdateBudget,
  type BudgetMetric, type BudgetRuleView,
} from '../lib/queries';
import BudgetForm from '../components/BudgetForm';
import AlertsFeed from '../components/AlertsFeed';

/**
 * Cost renders as USD; token and turn counts render grouped; a cadence rule's
 * threshold renders in MINUTES, with the unit, because `2880` next to `$10.00`
 * and `1,000,000` is otherwise unreadable.
 *
 * A `switch` with an exhaustiveness guard, not the `if` chain it replaces: a
 * widened `BudgetMetric` silently falls into a trailing `return`, which is how
 * a cadence threshold would have rendered as a bare count. A fifth metric must
 * be a COMPILE error here, not a coin flip on whether the fall-through happens
 * to be right for it.
 */
export function fmtThreshold(metric: BudgetMetric, n: number): string {
  switch (metric) {
    case 'cost_usd':
      return `$${n.toFixed(2)}`;
    case 'total_tokens':
      return n.toLocaleString();
    case 'turn_count':
      return String(n);
    case 'silence_minutes':
      return `${n.toLocaleString()} min`;
    default: {
      const exhaustive: never = metric;
      return String(exhaustive);
    }
  }
}

const METRIC_LABELS: Record<BudgetMetric, string> = {
  cost_usd: 'cost',
  total_tokens: 'tokens',
  turn_count: 'turns',
  silence_minutes: 'silence',
};

/**
 * The rule's kind as one cell. Exhaustive for the same reason `fmtThreshold`
 * is: the ternary this replaces read `kind === 'period' ? … : 'run'`, which
 * would have labelled a cadence rule **"run"** — a wrong answer that no type
 * check could have caught, since the else-arm absorbs every new member.
 */
export function fmtKind(rule: BudgetRuleView): string {
  switch (rule.kind) {
    case 'period':
      return `period / ${rule.period}`;
    case 'run':
      return 'run';
    case 'cadence':
      return 'cadence';
    default: {
      const exhaustive: never = rule.kind;
      return String(exhaustive);
    }
  }
}

/**
 * The rule's scope as one cell: its `scope_producer`, or "all" when unscoped.
 *
 * A rule stored before the producer cutover may still carry a string
 * `scope_platform`. The evaluator DROPS such a rule on every pass — it is not
 * evaluated at all, whatever else it carries — so rendering the platform as a
 * live scope, or the rule as "all", would tell its owner a guard is watching
 * when nothing is. It renders as a muted RETIRED pill instead, and wins over any
 * `scope_producer` the row also holds, because that producer scope is not being
 * read either. No edit can clear it (the API refuses the field even as `null`);
 * the edit form says to delete and recreate.
 */
function ScopeCell({ rule }: { rule: BudgetRuleView }) {
  if (typeof rule.scope_platform === 'string') {
    return (
      <span
        className="pill pill-muted budget-scope-retired"
        title="Scoped by a retired platform: this rule is not evaluated. Delete it and create it again scoped by producer."
      >
        retired platform: {rule.scope_platform}
      </span>
    );
  }
  return rule.scope_producer !== undefined
    ? <span className="mono">{rule.scope_producer}</span>
    : <span className="note">all</span>;
}

export default function BudgetsTab() {
  const { data, isLoading, isError, error, refetch } = useBudgets();
  const update = useUpdateBudget();
  const remove = useDeleteBudget();

  const [formFor, setFormFor] = useState<{ mode: 'new' } | { mode: 'edit'; rule: BudgetRuleView } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const rules = data?.budgets ?? [];

  const run = async (fn: () => Promise<unknown>) => {
    setMutationError(null);
    try { await fn(); } catch (err) {
      setMutationError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="budgets-tab" aria-label="Budgets">
      <div className="budgets-toolbar">
        <button
          type="button"
          className="btn"
          onClick={() => { setConfirmDelete(null); setFormFor({ mode: 'new' }); }}
        >
          New rule
        </button>
      </div>

      {formFor && (
        <BudgetForm
          {...(formFor.mode === 'edit' ? { existing: formFor.rule } : {})}
          onDone={() => setFormFor(null)}
          onCancel={() => setFormFor(null)}
        />
      )}

      {mutationError && <p className="error" role="alert">Action failed: {mutationError}</p>}

      <h3 className="section-title">Rules</h3>
      {isLoading ? (
        <p className="note">Loading budget rules…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load budget rules: {error instanceof Error ? error.message : 'unknown error'}{' '}
          <button type="button" className="btn" onClick={() => refetch()}>Retry</button>
        </p>
      ) : rules.length === 0 ? (
        <p className="note">No budget rules yet. Create one to start watching cost and tokens.</p>
      ) : (
        <table className="detail-table budgets-table">
          <thead>
            <tr>
              <th>name</th>
              <th>kind</th>
              <th>metric</th>
              <th className="detail-num">threshold</th>
              <th>scope</th>
              <th>webhook</th>
              <th>state</th>
              <th>actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.rule_id}>
                <td>{rule.name}</td>
                <td>{fmtKind(rule)}</td>
                <td>{METRIC_LABELS[rule.metric]}</td>
                <td className="detail-num mono">{fmtThreshold(rule.metric, rule.threshold)}</td>
                <td><ScopeCell rule={rule} /></td>
                <td>
                  {rule.webhook_url
                    ? <span className="pill">{rule.webhook_secret_set ? 'signed' : 'unsigned'}</span>
                    : <span className="note">none</span>}
                </td>
                <td>
                  <span className={rule.enabled ? 'pill' : 'pill pill-muted'}>
                    {rule.enabled ? 'enabled' : 'disabled'}
                  </span>
                </td>
                <td className="budgets-actions">
                  <button
                    type="button"
                    className="btn"
                    aria-label={`${rule.enabled ? 'Disable' : 'Enable'} ${rule.name}`}
                    onClick={() => void run(() =>
                      update.mutateAsync({ ruleId: rule.rule_id, patch: { enabled: !rule.enabled } }))}
                  >
                    {rule.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    aria-label={`Edit ${rule.name}`}
                    onClick={() => { setConfirmDelete(null); setFormFor({ mode: 'edit', rule }); }}
                  >
                    Edit
                  </button>
                  {confirmDelete === rule.rule_id ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      aria-label={`Confirm delete ${rule.name}`}
                      onClick={() => void run(async () => {
                        await remove.mutateAsync(rule.rule_id);
                        setConfirmDelete(null);
                      })}
                    >
                      Confirm
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      aria-label={`Delete ${rule.name}`}
                      onClick={() => setConfirmDelete(rule.rule_id)}
                      // Arming a different row's delete implicitly disarms any
                      // other row — only one row may carry the destructive,
                      // irreversible "Confirm" state at a time.
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AlertsFeed />
    </section>
  );
}
